"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.currencyAndTotalQuery = exports.virtualCardDataQuery = exports.virtualCardQuery = exports.useEnrollCardV2Query = exports.useGetTeamsForCardSourceQuery = exports.useGetCardSourceQuery = exports.useGetTeamCardSourcesQuery = exports.useGetCompanyCardSourcesQuery = exports.useGetTeamApproversQuery = exports.useRemoveCardSourceFromTeamQuery = exports.useAddCardSourceToTeamQuery = exports.useSetCardNameQuery = exports.useGetCardSourcesQuery = exports.paymentParams = exports.paymentKeys = void 0;
const react_query_1 = require("@tanstack/react-query");
const lodash_1 = require("lodash");
const pino_1 = __importDefault(require("pino"));
const paymentRepository_1 = require("../repositories/paymentRepository");
const Root_1 = require("../router/Root");
const i18n_1 = __importDefault(require("../utils/i18n"));
const zustandStore_1 = require("../zustandStore");
const slices_1 = require("../zustandStore/slices");
const companyQuery_1 = require("./companyQuery");
const userQuery_1 = require("./userQuery");
const logger = (0, pino_1.default)();
exports.paymentKeys = {
    getCardSources: 'getCardSources',
    getTeamApprovers: 'getTeamApprovers',
    getCardSource: 'getCardSource',
    getTeamsForCardSource: 'getTeamsForCardSource',
    setCardName: 'setCardName',
    addCardSourceToTeam: 'addCardSourceToTeam',
    removeCardSourceFromTeam: 'removeCardSourceFromTeam',
    getEnrollmentTokenV2: 'getEnrollmentTokenV2',
    getVirtualCardToken: (documentId) => ['getVirtualCardToken', documentId],
    getVirtualCard: (documentId, cardProvider) => ['getVirtualCard', documentId, cardProvider],
    getCurrencyAndTotal: (currency, value) => ['getCurrencyAndTotal', currency, value],
    getVirtualCardInfo: (documentId) => ['getVirtualCardInfo', documentId],
    getCompanyCardSources: (includeTeamsCount = true) => ['companyCardSources', { includeTeamsCount }],
    getTeamCardSources: (params) => ['teamCardSources', params],
    getVirtualCardData: (documentId, isCardOwner) => ['getVirtualCardData', documentId, isCardOwner],
};
exports.paymentParams = {
    includeTeamsCount: true,
};
const useGetCardSourcesQuery = ({ includeTeamsCount = true, status = '', teamId = '', } = {}) => {
    const params = {
        includeTeamsCount,
        status,
    };
    return (0, react_query_1.useQuery)({
        queryKey: [exports.paymentKeys.getCardSources, { includeTeamsCount, status, teamId }, params],
        queryFn: () => paymentRepository_1.PaymentRepository.getCardSources(params, teamId),
    });
};
exports.useGetCardSourcesQuery = useGetCardSourcesQuery;
const useSetCardNameQuery = ({ shouldSetOnCompanyCard = true }) => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: paymentRepository_1.PaymentRepository.setCardName,
        onMutate: async ({ cardSourceId, name, teamId }) => {
            await (shouldSetOnCompanyCard
                ? queryClient.cancelQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources(true)] })
                : queryClient.cancelQueries({
                    queryKey: [
                        exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true }),
                    ],
                }));
            const previousData = queryClient.getQueryData(shouldSetOnCompanyCard
                ? [exports.paymentKeys.getCompanyCardSources(true)]
                : [exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true })]);
            const previousCardSourceData = [
                ...(previousData ?? []),
            ].find(data => data && data.id === cardSourceId);
            if (previousCardSourceData) {
                queryClient.setQueryData(shouldSetOnCompanyCard
                    ? [exports.paymentKeys.getCompanyCardSources(true)]
                    : [exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true })], (prev) => {
                    const updated = prev.map(data => {
                        if (data.id === cardSourceId) {
                            return { ...data, name };
                        }
                        return data;
                    });
                    return updated;
                });
            }
            return { previousCardSourceData };
        },
        onError: (err, variables, context) => {
            logger.error(err);
            if (context?.previousCardSourceData) {
                queryClient.setQueryData(shouldSetOnCompanyCard
                    ? [exports.paymentKeys.getCompanyCardSources(true)]
                    : [
                        exports.paymentKeys.getTeamCardSources({
                            includeTeamsCount: true,
                            teamId: variables.teamId || '',
                            isEnabled: true,
                        }),
                    ], (prev) => {
                    const updated = prev.data?.map(data => data.id === context.previousCardSourceData?.id ? context.previousCardSourceData : data);
                    return { ...prev, data: updated };
                });
            }
        },
    });
};
exports.useSetCardNameQuery = useSetCardNameQuery;
const useAddCardSourceToTeamQuery = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: paymentRepository_1.PaymentRepository.addCardSourceToTeam,
        onMutate: async ({ cardSourceId }) => {
            await queryClient.cancelQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources(true)] });
            const prevData = queryClient.getQueryData([exports.paymentKeys.getCompanyCardSources(true)]);
            if (prevData) {
                const updatedData = prevData.map(card => {
                    if (card.id === cardSourceId) {
                        return { ...card, teamsCount: (Number.parseInt(card.teamsCount, 10) + 1).toString() };
                    }
                    return card;
                });
                queryClient.setQueryData([exports.paymentKeys.getCompanyCardSources(true)], updatedData);
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources] });
            void queryClient.invalidateQueries({ queryKey: [exports.paymentKeys.getTeamsForCardSource] });
        },
    });
};
exports.useAddCardSourceToTeamQuery = useAddCardSourceToTeamQuery;
const useRemoveCardSourceFromTeamQuery = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: paymentRepository_1.PaymentRepository.removeCardSourceFromTeam,
        onMutate: async ({ cardSourceId }) => {
            await queryClient.cancelQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources(true)] });
            const prevData = queryClient.getQueryData([exports.paymentKeys.getCompanyCardSources(true)]);
            if (prevData) {
                const updatedData = prevData.map(card => {
                    if (card.id === cardSourceId) {
                        return { ...card, teamsCount: (Number.parseInt(card.teamsCount, 10) - 1).toString() };
                    }
                    return card;
                });
                queryClient.setQueryData([exports.paymentKeys.getCompanyCardSources(true)], updatedData);
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources] });
            void queryClient.invalidateQueries({ queryKey: [exports.paymentKeys.getTeamsForCardSource] });
        },
    });
};
exports.useRemoveCardSourceFromTeamQuery = useRemoveCardSourceFromTeamQuery;
const useGetTeamApproversQuery = ({ excludeSelf, excludeOutOfOffice, teamId }) => {
    const { setTeamApprovers } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [exports.paymentKeys.getTeamApprovers, excludeSelf, teamId, excludeOutOfOffice],
        queryFn: async () => {
            const result = await paymentRepository_1.PaymentRepository.getTeamApprovers({
                excludeSelf,
                excludeOutOfOffice: excludeOutOfOffice || false,
                teamId,
            });
            setTeamApprovers(result);
            return result;
        },
        enabled: Boolean(teamId),
    });
};
exports.useGetTeamApproversQuery = useGetTeamApproversQuery;
const getCompanyCardSourcesQuery = ({ includeTeamsCount = true, isEnabled = true }) => ({
    queryKey: [exports.paymentKeys.getCompanyCardSources(includeTeamsCount)],
    queryFn: () => paymentRepository_1.PaymentRepository.getCompanyCardSources({
        includeTeamsCount,
    }),
    enabled: isEnabled ?? true,
});
const useGetCompanyCardSourcesQuery = ({ includeTeamsCount = true, isEnabled = true }) => {
    return (0, react_query_1.useQuery)(getCompanyCardSourcesQuery({ includeTeamsCount, isEnabled }));
};
exports.useGetCompanyCardSourcesQuery = useGetCompanyCardSourcesQuery;
const useGetTeamCardSourcesQuery = (params) => {
    const { setTeamCards } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [exports.paymentKeys.getTeamCardSources(params)],
        queryFn: async () => {
            const result = await paymentRepository_1.PaymentRepository.getTeamCardSources(params);
            setTeamCards(result);
            return result;
        },
        enabled: params.isEnabled ?? true,
    });
};
exports.useGetTeamCardSourcesQuery = useGetTeamCardSourcesQuery;
const useGetCardSourceQuery = ({ cardSourceId, includeCardHolder, teamId, }) => {
    const { setCardSource } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [exports.paymentKeys.getCardSource, cardSourceId, includeCardHolder, teamId],
        queryFn: async () => {
            const result = await paymentRepository_1.PaymentRepository.getCardSource({ cardSourceId, includeCardHolder, teamId: teamId || '' });
            setCardSource(result);
            return result;
        },
        enabled: !!cardSourceId,
    });
};
exports.useGetCardSourceQuery = useGetCardSourceQuery;
const useGetTeamsForCardSourceQuery = ({ cardSourceId }) => {
    const { setCardSourceTeams } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [exports.paymentKeys.getTeamsForCardSource, cardSourceId],
        queryFn: async () => {
            const result = await paymentRepository_1.PaymentRepository.getTeamsForCardSource(cardSourceId);
            setCardSourceTeams(result);
            return result;
        },
        enabled: Boolean(cardSourceId),
    });
};
exports.useGetTeamsForCardSourceQuery = useGetTeamsForCardSourceQuery;
async function enrollCard(enrollData, cardProvider, teamId) {
    if (!enrollData || (0, lodash_1.isEmpty)(enrollData)) {
        throw new TypeError('formData is empty');
    }
    if (!cardProvider) {
        throw new TypeError('cardProvider is empty');
    }
    const data = await paymentRepository_1.PaymentRepository.getEnrollmentToken({ teamId });
    const enrollment = {
        enrollmentToken: data.token,
        paymentInformation: cardProvider === 'HSBC' ? (0, slices_1.buildHsbcPaymentInformation)(enrollData) : (0, slices_1.buildAmexPaymentInformation)(enrollData),
    };
    const result = cardProvider === 'HSBC'
        ? await paymentRepository_1.PaymentRepository.enrollWithHsbc(enrollment)
        : await paymentRepository_1.PaymentRepository.enrollWithAmex(enrollment);
    const cardSource = cardProvider === 'HSBC' ? (0, slices_1.buildHsbcCardSource)(enrollData, result) : (0, slices_1.buildAmexCardSource)(enrollData, result);
    return { cardSource };
}
const mapUiCardDataToBaseStructure = (obj) => {
    if (!obj) {
        return null;
    }
    return {
        billingAddress: {
            addressLine1: obj.address,
            city: obj.city,
            countryCode: obj.countryCode,
            region: obj.regionCode,
            zipCode: obj.zipCode,
        },
        id: obj.id,
        lastDigits: obj.cardNumber.slice(-4),
        name: obj.name,
        status: 'IN_PROGRESS',
        label: `${obj.name}, ···· ${obj.cardNumber.slice(-4)}`,
        teamsCount: '0',
        userId: obj.userId,
    };
};
const mapUiCardDataToAmexStructure = (obj) => {
    const baseStructure = mapUiCardDataToBaseStructure(obj);
    if (!baseStructure) {
        return null;
    }
    return {
        ...baseStructure,
        billingAddress: {
            ...baseStructure.billingAddress,
            countryCode: 'US',
        },
        billingCurrency: obj.billingCurrency || 'USD',
        provider: 'AMEX',
        providerLabel: 'American Express',
    };
};
const mapUiCardDataToHsbcStructure = (obj) => {
    const baseStructure = mapUiCardDataToBaseStructure(obj);
    if (!baseStructure) {
        return null;
    }
    return {
        ...baseStructure,
        billingAddress: {
            ...baseStructure.billingAddress,
            countryCode: 'GB',
        },
        billingCurrency: obj.billingCurrency || 'GBP',
        provider: 'HSBC',
        providerLabel: 'HSBC',
    };
};
const useEnrollCardV2Query = () => {
    return (0, react_query_1.useMutation)({
        mutationFn: ({ enrollData, cardProvider, teamId, }) => enrollCard(enrollData, cardProvider, teamId),
        onMutate: async (variables) => {
            // we need this as back-end returns old data after responding ok
            await (variables.teamId
                ? Root_1.queryClient.cancelQueries({
                    queryKey: [
                        exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true }),
                    ],
                })
                : Root_1.queryClient.cancelQueries({ queryKey: [exports.paymentKeys.getCompanyCardSources(true)] }));
            const previousData = Root_1.queryClient.getQueryData(variables.teamId
                ? [exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
                : [exports.paymentKeys.getCompanyCardSources(true)]);
            if (previousData) {
                Root_1.queryClient.setQueryData(variables.teamId
                    ? [exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
                    : [exports.paymentKeys.getCompanyCardSources(true)], (prev) => {
                    const enrollData = variables.enrollData;
                    const updated = [
                        ...(prev ?? []),
                        variables.cardProvider === 'HSBC'
                            ? mapUiCardDataToHsbcStructure(enrollData)
                            : mapUiCardDataToAmexStructure(enrollData),
                    ];
                    return updated;
                });
            }
            return { previousData };
        },
        onError: (_error, variables, context) => {
            if (context?.previousData) {
                Root_1.queryClient.setQueryData(variables.teamId
                    ? [exports.paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
                    : [exports.paymentKeys.getCompanyCardSources(true)], context.previousData);
            }
        },
    });
};
exports.useEnrollCardV2Query = useEnrollCardV2Query;
const virtualCardQuery = ({ documentId, cardProvider, }) => (0, react_query_1.queryOptions)({
    queryKey: exports.paymentKeys.getVirtualCard(documentId, cardProvider || 'N/A'),
    queryFn: async () => {
        const data = await paymentRepository_1.PaymentRepository.getVirtualCardToken(documentId);
        return paymentRepository_1.PaymentRepository.getVirtualCard(data?.token, cardProvider, data?.tokenSource);
    },
    enabled: Boolean(documentId && cardProvider),
});
exports.virtualCardQuery = virtualCardQuery;
const getVirtualCardData = async (documentId, isCardOwner) => {
    if (isCardOwner) {
        const user = await Root_1.queryClient.ensureQueryData(userQuery_1.userQuery);
        const companyPropertiesData = await Root_1.queryClient.ensureQueryData((0, companyQuery_1.companyPropertiesQuery)(user?.companyAccountId || '', { cardprovider: 'tsgo-cardprovider' }));
        const cardProvider = companyPropertiesData?.cardprovider || '';
        const { token, tokenSource } = await paymentRepository_1.PaymentRepository.getVirtualCardToken(documentId);
        return paymentRepository_1.PaymentRepository.getVirtualCard(token, cardProvider, tokenSource);
    }
    return paymentRepository_1.PaymentRepository.getVirtualCardInfo({ documentId });
};
const virtualCardDataQuery = (documentId, isCardOwner) => (0, react_query_1.queryOptions)({
    queryKey: exports.paymentKeys.getVirtualCardData(documentId, isCardOwner),
    queryFn: () => getVirtualCardData(documentId, isCardOwner),
    enabled: Boolean(documentId),
    staleTime: 0,
});
exports.virtualCardDataQuery = virtualCardDataQuery;
const currencyAndTotalQuery = (currency, value) => {
    return (0, react_query_1.queryOptions)({
        queryKey: exports.paymentKeys.getCurrencyAndTotal(currency, value),
        queryFn: async () => {
            await i18n_1.default.setLanguage('en');
            const currencySymbol = i18n_1.default.getCurrencySymbol(currency || 'USD');
            const numericValue = typeof value === 'string' ? i18n_1.default.parseAmount(value) : value;
            const newValue = i18n_1.default.formatNumber(numericValue);
            return `${currencySymbol}${newValue}`;
        },
        enabled: Boolean(currency) && value != null,
    });
};
exports.currencyAndTotalQuery = currencyAndTotalQuery;
